// The way in. board.contextbolt.com opens on sign-in: an email, then a code of
// eight letters and digits (0-9 and A-F). A known email goes straight back to
// its board; a new one picks how its first board starts, and that is the
// account. Simple on purpose: this page signs people in, it does not sell.

import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import type { Choice } from "../../shared/onboarding";
import { StartQuestion } from "../onboarding/Question";
import { CODE_LENGTH, cleanCode, clock, resendWait } from "../../shared/signinCode";
import { api, getToken, setToken, type ApiError } from "../lib/api";
import { loadTurnstile } from "../lib/turnstile";
import { cx } from "../lib/util";
import { boot, startWorkspace } from "../state/sync";
import { bootShared, setViewer, sharedTarget } from "../state/guest";
import { set } from "../state/store";
import { Backdrop } from "../themes/Backdrop";
import { resolveTheme, THEME } from "../themes/themes";
import { Logo } from "./Logo";
import { signinTheme } from "./signinTheme";
import { checkoutFor, BILLING_PORTAL, type BillingStatus } from "../../shared/billing";

/** shown while the bot check fails; it clears itself once a token comes */
const CHECK_PROBLEM = "The bot check hit a problem. If this stays, reload the page.";


// A code works only on the page that asked for it (its flow). The flow is
// kept for this tab, so a reload while the email is on its way keeps the step,
// with how many codes went to the address and when the last one did, which
// pace the next. Signing in or out clears it (setToken).
const FLOW_KEY = "cb:signin";
type KeptFlow = { email: string; flow: string; sent: number; at: number };
const readFlow = (): KeptFlow | null => {
  try {
    const v = JSON.parse(sessionStorage.getItem(FLOW_KEY) ?? "null") as { email?: unknown; flow?: unknown; sent?: unknown; at?: unknown } | null;
    return v && typeof v.email === "string" && typeof v.flow === "string" ? { email: v.email, flow: v.flow, sent: Number(v.sent) || 1, at: Number(v.at) || Date.now() } : null;
  } catch {
    return null;
  }
};
const keepFlow = (v: KeptFlow | null) => {
  try {
    if (v) sessionStorage.setItem(FLOW_KEY, JSON.stringify(v));
    else sessionStorage.removeItem(FLOW_KEY);
  } catch {
    /* private mode: the step just will not survive a reload */
  }
};

/**
 * Email, then code. A known email signs in and calls `onSignedIn` (or boots).
 * An email with no board yet calls `onFresh` with the one-use ticket that
 * starts one.
 */
export function SignIn({
  title = "Sign in to Context Board",
  sub = "We'll email you a code. New here? The same code starts your board.",
  onSignedIn,
  onFresh,
}: {
  title?: string;
  sub?: string;
  onSignedIn?: () => void;
  onFresh: (ticket: string, email: string, shared: boolean) => void;
}) {
  const kept = useMemo(readFlow, []);
  const [email, setEmail] = useState(kept?.email ?? "");
  const [flow, setFlow] = useState<string | null>(kept?.flow ?? null);
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">(kept ? "code" : "email");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  // codes sent to one address: each new one waits longer, since an address gets three an hour from one network
  const [sent, setSent] = useState(kept ? { to: kept.email, n: kept.sent } : { to: "", n: 0 });
  const [wait, setWait] = useState(kept ? Math.max(0, resendWait(kept.sent) - Math.floor((Date.now() - kept.at) / 1000)) : 0);
  // refused for asking too often: no more offers on this step; the error says when to try again
  const [limited, setLimited] = useState(false);
  // the code last checked, which is never sent again by itself
  const tried = useRef<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  // the bot check (Cloudflare Turnstile): its sitekey when the server has it on, the
  // token it gave, and whether Cloudflare wants a click. A token is good for one code
  const [sitekey, setSitekey] = useState<string | null>(null);
  const [human, setHuman] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const humanBox = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const checking = !!sitekey && !human;

  useEffect(() => {
    let gone = false;
    // without the options the server still refuses an unchecked code, and says so
    api<{ turnstile?: string | null }>("/api/login/options", { token: null })
      .then((r) => !gone && r.turnstile && setSitekey(r.turnstile))
      .catch(() => {});
    return () => {
      gone = true;
    };
  }, []);

  useEffect(() => {
    if (!sitekey) return;
    let gone = false;
    loadTurnstile()
      .then((ts) => {
        if (gone || !humanBox.current) return;
        widget.current = ts.render(humanBox.current, {
          sitekey,
          action: "signin",
          appearance: "interaction-only",
          theme: "dark",
          size: "flexible",
          callback: (token: string) => {
            setHuman(token);
            setErr((e) => (e === CHECK_PROBLEM ? null : e));
          },
          "expired-callback": () => setHuman(null),
          // handled here, so Cloudflare does not throw; it keeps retrying by itself
          "error-callback": () => {
            setHuman(null);
            setErr(CHECK_PROBLEM);
            return true;
          },
          "before-interactive-callback": () => setAsking(true),
          "after-interactive-callback": () => setAsking(false),
        });
      })
      .catch((e: Error) => !gone && setErr(`${e.message} Check your connection, then reload the page.`));
    return () => {
      gone = true;
      if (widget.current) window.turnstile?.remove(widget.current);
      widget.current = null;
    };
  }, [sitekey]);

  // a token is spent by the code it asked for, so the next code needs a fresh one
  const freshCheck = () => {
    if (!widget.current) return;
    setHuman(null);
    window.turnstile?.reset(widget.current);
  };

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const send = async () => {
    if (checking) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ flow: string; devCode?: string }>("/api/login/start", { body: { email, turnstile: human ?? undefined }, token: null });
      const to = email.trim().toLowerCase();
      const n = to === sent.to ? sent.n + 1 : 1;
      setFlow(r.flow);
      keepFlow({ email: to, flow: r.flow, sent: n, at: Date.now() });
      setSent({ to, n });
      setDevCode(r.devCode ?? null);
      setCode("");
      tried.current = null;
      setLimited(false);
      setStep("code");
      setWait(resendWait(n));
    } catch (e) {
      setErr((e as Error).message);
      if ((e as ApiError).status === 429) setLimited(true);
    } finally {
      setBusy(false);
      freshCheck();
    }
  };

  const verify = async (value = code) => {
    if (busy || !flow) return;
    tried.current = value;
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ token?: string; fresh?: boolean; ticket?: string; email?: string; shared?: boolean }>("/api/login/verify", { body: { flow, code: value }, token: null });
      keepFlow(null);
      if (r.fresh && r.ticket) {
        onFresh(r.ticket, r.email ?? email, r.shared === true);
        return;
      }
      setToken(r.token ?? null);
      if (onSignedIn) onSignedIn();
      else {
        // signed in from a link to a board shared with them: that board opens (Features 1, sharing)
        const target = sharedTarget();
        await (target ? bootShared(target) : boot());
      }
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
      // a used-up or expired code needs a new one: offer it at once
      if ((e as ApiError).code === "restart") setWait(0);
      codeRef.current?.select();
    }
  };

  return (
    <form
      className="signin island"
      onSubmit={(e) => {
        e.preventDefault();
        void (step === "email" ? send() : verify());
      }}
    >
      <h3>{step === "email" ? title : "Check your email"}</h3>
      <p>
        {step === "email" ? (
          sub
        ) : (
          <>
            {sent.n > 1 ? (
              <>
                We sent a new code to <b>{email}</b>. Only the newest code works. It expires in 10 minutes.
              </>
            ) : (
              <>
                We sent a code to <b>{email}</b>. Enter it here. It expires in 10 minutes.
              </>
            )}
          </>
        )}
      </p>
      {step === "email" ? (
        <label className="signin-field">
          <span className="sr-only">Email</span>
          <input type="email" name="email" autoComplete="email" autoFocus required placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
      ) : (
        <label className="signin-field">
          <span className="sr-only">Code from the email</span>
          <input
            ref={codeRef}
            name="code"
            autoComplete="one-time-code"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            placeholder="Code"
            value={code}
            className="code-input"
            onChange={(e) => {
              const v = cleanCode(e.target.value, code);
              setCode(v);
              // the whole code: sign in without a click, unless it is the code just refused
              if (v.length === CODE_LENGTH && v !== tried.current) void verify(v);
            }}
          />
        </label>
      )}
      {devCode && step === "code" && <p className="note">Development server: the code is {devCode}.</p>}
      {sitekey && <div ref={humanBox} className={cx("signin-human", !asking && "is-quiet")} />}
      {err && (
        <p className="signin-err" role="alert">
          {err}
        </p>
      )}
      <button type="submit" className="btn btn-primary signin-go" disabled={busy || (step === "code" ? code.length !== CODE_LENGTH : checking)}>
        {busy ? <span className="spinner" /> : step === "email" ? "Email me a code" : "Sign in"}
      </button>
      {step === "code" && (
        <div className="signin-links">
          <button
            type="button"
            className="link-btn"
            onClick={() => {
              setStep("email");
              setErr(null);
              setDevCode(null);
              setLimited(false);
              keepFlow(null);
            }}
          >
            Use a different email
          </button>
          <button type="button" className="link-btn" disabled={wait > 0 || busy || limited || checking} onClick={() => void send()}>
            {wait > 0 && !limited ? `Send a new code in ${clock(wait)}` : "Send a new code"}
          </button>
        </div>
      )}
    </form>
  );
}

/**
 * A new account's first choice: how its board starts. The ticket is what makes
 * it an account, and the address it was sent to is named, in case it was the
 * wrong one.
 */
/** Boards were shared with this new email: they open free, with no board of its own (Features 1, sharing). */
function OpenShared({ ticket }: { ticket: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const open = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ token: string }>("/api/viewer/start", { body: { ticket }, token: null });
      setViewer(r.token);
      keepTicket(null);
      const target = sharedTarget();
      if (target) await bootShared(target);
      else set({ phase: "shared" });
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div className="open-shared island">
      <p>
        <b>Boards were shared with you.</b> Open them now, free, with no board of your own.
      </p>
      <button className="btn btn-primary" disabled={busy} onClick={() => void open()}>
        Open what's shared with you {busy ? <span className="spinner" /> : <ArrowRight size={14} />}
      </button>
      {err && <p className="signin-err">{err}</p>}
    </div>
  );
}

function PickStart({ ticket, email, shared, onCancel }: { ticket?: string; email?: string; shared?: boolean; onCancel: () => void }) {
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [checkingBilling, setCheckingBilling] = useState(!!ticket);
  const [busy, setBusy] = useState<Choice | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const checkBilling = async () => {
    if (!ticket) return;
    setCheckingBilling(true);
    setErr(null);
    // a check that answers clears what an earlier one said: two at once (a double click) hold the same ticket, and one is refused
    try { setBilling(await api<BillingStatus>("/api/billing/entitlement", { body: { ticket }, token: null })); setErr(null); }
    catch (e) { setErr((e as Error).message); }
    finally { setCheckingBilling(false); }
  };
  useEffect(() => { void checkBilling(); }, [ticket]);
  const start = async (id: Choice) => {
    setBusy(id);
    setErr(null);
    // a key saved before this start can be another tab's: only one that changed during it can be this start's
    const before = getToken();
    try {
      // the answer makes the first board (Features 4); Skip is today's first board, Founder HQ
      await startWorkspace("founder", ticket, id);
      keepTicket(null);
    } catch (e) {
      // the board was made and its key saved, and only loading it failed: open it rather than spend the ticket again
      const saved = getToken();
      if (saved && saved !== before) {
        keepTicket(null);
        await boot();
        setBusy(null);
        return;
      }
      // a ticket that has run out or was used cannot start a board, so it is not kept for a reload
      if ((e as ApiError).code === "signin_expired") keepTicket(null);
      setErr((e as Error).message);
      setBusy(null);
    }
  };
  if (ticket && (checkingBilling || !billing || !billing.allowed)) return (
    <>
      {shared && <OpenShared ticket={ticket} />}
      <h1>Your board<br /><em>starts here.</em></h1>
      <p className="welcome-sub">Free for 3 days, then $12 a month. Card required. Cancel anytime. Taxes may apply.</p>
      {checkingBilling ? <p className="note">Checking your subscription...</p> : !billing ? <button className="btn" onClick={() => void checkBilling()}>Check again</button> : <>
        <p className="note">Use <b>{email}</b> at checkout, then return here. Your own AI account is required for agent features.</p>
        <a className="btn btn-primary" href={billing?.status === "none" ? checkoutFor(email) : BILLING_PORTAL}>{billing?.status === "none" ? "Start free trial" : "Manage subscription"}</a>
        <button className="btn" style={{ marginLeft: 10 }} onClick={() => void checkBilling()}>I've checked out. Check again.</button>
      </>}
      {err && <p className="signin-err">{err}</p>}
      <button type="button" className="link-btn welcome-back" onClick={onCancel}>Back to sign in</button>
    </>
  );
  return (
    <>
      {shared && ticket && <OpenShared ticket={ticket} />}
      <StartQuestion email={email} busy={busy} onPick={(id) => void start(id)} />
      {err && <p className="signin-err">{err}</p>}
      <button type="button" className="link-btn welcome-back" onClick={onCancel}>
        Back to sign in
      </button>
    </>
  );
}

// A new email's ticket and its address, kept for this tab, so a reload keeps
// the step that spends it, here and on the Allow page. Signing in or out
// clears it (setToken).
const TICKET_KEY = "cb:ticket";
export type KeptTicket = { ticket: string; email: string; shared?: boolean };
export const keepTicket = (t: KeptTicket | null) => {
  try {
    if (t) sessionStorage.setItem(TICKET_KEY, JSON.stringify(t));
    else sessionStorage.removeItem(TICKET_KEY);
  } catch {
    /* private mode */
  }
};
export const readTicket = (): KeptTicket | null => {
  try {
    const v = JSON.parse(sessionStorage.getItem(TICKET_KEY) ?? "null") as { ticket?: unknown; email?: unknown } | null;
    return v && typeof v.ticket === "string" && typeof v.email === "string" ? { ticket: v.ticket, email: v.email, shared: (v as { shared?: unknown }).shared === true } : null;
  } catch {
    return null;
  }
};

export function Welcome() {
  // a different scene each day, kept for as long as the page is open
  const [scene] = useState(signinTheme);
  const theme = resolveTheme({ id: scene });
  // signing in, or picking how the board starts: a new account (with its ticket), or on the dev server no account at all
  const [ticket, setTicket] = useState<KeptTicket | null>(readTicket);
  const [stage, setStage] = useState<"signin" | "pick">(ticket ? "pick" : "signin");
  return (
    <div className={cx("board-root welcome", stage === "pick" && "is-asking")} data-mode="dark" style={theme.vars as React.CSSProperties}>
      <Backdrop def={THEME[scene]} motion={false} dim={0.1} clearTop />
      <header className="welcome-top">
        <Logo />
        <a className="welcome-link" href="https://contextbolt.com/board/">
          What is Context Board?
        </a>
      </header>
      {stage === "signin" ? (
        <main className="signin-stage">
          <SignIn
            onFresh={(t, email, shared) => {
              const kept = { ticket: t, email, shared };
              keepTicket(kept);
              setTicket(kept);
              setStage("pick");
            }}
          />
          {import.meta.env.DEV && (
            <button type="button" className="link-btn dev-open" onClick={() => setStage("pick")}>
              Development server: start without signing in
            </button>
          )}
        </main>
      ) : (
        <main className="welcome-main is-question">
          <PickStart
            ticket={ticket?.ticket}
            email={ticket?.email}
            shared={ticket?.shared}
            onCancel={() => {
              keepTicket(null);
              setTicket(null);
              setStage("signin");
            }}
          />
        </main>
      )}
    </div>
  );
}
